import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Paper, SelectChangeEvent } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useMeContext } from 'modules/me';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';
import { useMessages } from 'modules/intl';
import { DropdownLabel, getDaterange } from './utils';
import MyTeamHoursLegend from './MyTeamHoursLegend';
import HoursChart from './HoursChart';
import TableUtilizationChart from './TableUtilizationChart';
import BillableUtilization from './BillableUtilization';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    padding: theme.spacing(2),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(2),
    flexDirection: 'column',
    width: '100%'
  }
}));
const DropdownLabelName = [
  `insights.teamTable.${DropdownLabel?.hours}`,
  `insights.teamTable.${DropdownLabel?.utilization}`,
  `insights.teamTable.${DropdownLabel?.billable}`
];

const TeamChart: React.FC = () => {
  const { currentPeriod } = useSupervisorInsightContext();
  const optionNames = useMessages(DropdownLabelName);

  const dropDownOptions = [
    {
      id: DropdownLabel.hours,
      name: optionNames[`insights.teamTable.${DropdownLabel.hours}`]
    },
    {
      id: DropdownLabel.utilization,
      name: optionNames[`insights.teamTable.${DropdownLabel.utilization}`]
    },
    {
      id: DropdownLabel.billable,
      name: optionNames[`insights.teamTable.${DropdownLabel.billable}`]
    }
  ];

  const [selectvalue, setSelectValue] = useState<string>(dropDownOptions[0].id);

  const onSelectValueChange = useCallback((event: SelectChangeEvent) => {
    setSelectValue(event.target.value);
  }, []);

  const [loading, setLoading] = useState(false);

  const [categoriesLabels, setCategoriesLabels] = useState<
    Record<string, string>
  >();
  const previousPeriod = useRef(currentPeriod);
  const me = useMeContext();

  const columns = useMemo(() => {
    const dateRange = getDaterange(currentPeriod);
    const formatpattern =
      me?.locale.dateTimeSettings.shortDateFormat.luxonPattern || 'MMMM d';
    const col = [...dateRange].map((i: string) =>
      DateTime.fromISO(i).toFormat(formatpattern)
    );
    return col;
  }, [currentPeriod, me?.locale.dateTimeSettings.shortDateFormat.luxonPattern]);

  const classes = useStyles();

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'my-team-chart'}
    >
      <MyTeamHoursLegend
        selectvalue={selectvalue}
        onSelectValueChange={onSelectValueChange}
        dropDownOptions={dropDownOptions}
        categoriesLabels={categoriesLabels}
        loading={loading}
        disabled={loading}
      />
      {selectvalue === DropdownLabel.hours && (
        <HoursChart
          columns={columns}
          previousPeriod={previousPeriod}
          setLoading={setLoading}
        />
      )}
      {selectvalue === DropdownLabel.utilization && (
        <TableUtilizationChart
          columns={columns}
          previousPeriod={previousPeriod}
          setLoading={setLoading}
          setCategoriesLabels={setCategoriesLabels}
        />
      )}
      {selectvalue === DropdownLabel.billable && (
        <BillableUtilization
          columns={columns}
          previousPeriod={previousPeriod}
          setLoading={setLoading}
        />
      )}
    </Paper>
  );
};
export default memo(TeamChart);
